import { useEffect, useState, useMemo } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { SlidersHorizontal, MapIcon, List, Search as SearchIcon } from "lucide-react";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Sheet, SheetContent, SheetTrigger, SheetTitle, SheetHeader } from "@/components/ui/sheet";
import ProviderCard from "@/components/ProviderCard";
import ProvidersMap from "@/components/ProvidersMap";
import { SERVICE_LIBRARY } from "@/lib/constants";
import { GitCompare, MapPin as MapPinIcon, Loader2, X as XIcon } from "lucide-react";
import { useGeo } from "@/lib/geo";

const TYPES = [
  { value: "all", label: "Any type" },
  { value: "mobile", label: "Mobile IV" },
  { value: "clinic", label: "IV Clinic" },
  { value: "both", label: "Mobile + Clinic" },
];

export default function Search() {
  const [params, setParams] = useSearchParams();
  const [results, setResults] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("split"); // list | split | map
  const [compareSlugs, setCompareSlugs] = useState([]);
  const { geo, status, requestPrecise, clear } = useGeo();
  const [useNearMe, setUseNearMe] = useState(false);

  const toggleCompare = (slug) => {
    setCompareSlugs((cur) => {
      if (cur.includes(slug)) return cur.filter((s) => s !== slug);
      if (cur.length >= 3) return cur;
      return [...cur, slug];
    });
  };

  const enableNearMe = async () => {
    // Always request precise browser location (overrides any IP-based estimate)
    const result = await requestPrecise();
    if (!result) {
      // Fall back to whatever geo we have (e.g., IP-based)
      if (!geo?.lat) return;
    }
    setUseNearMe(true);
  };

  const filters = useMemo(() => ({
    q: params.get("q") || "",
    city: params.get("city") || "",
    service: params.get("service") || "",
    type: params.get("type") || "all",
    min_rating: params.get("min_rating") || "",
    verified_only: params.get("verified_only") === "true",
    sort: params.get("sort") || "featured",
  }), [params]);

  const update = (patch) => {
    const next = new URLSearchParams(params);
    Object.entries(patch).forEach(([k, v]) => {
      if (v === "" || v === null || v === undefined || v === false || v === "all") next.delete(k);
      else next.set(k, v === true ? "true" : v);
    });
    setParams(next);
  };

  useEffect(() => {
    setLoading(true);
    const q = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => { if (v && v !== "all") q.set(k, v); });
    if (useNearMe && geo?.lat && geo?.lng) {
      q.set("lat", String(geo.lat));
      q.set("lng", String(geo.lng));
      q.set("radius_km", "800");
    }
    api.get(`/providers?${q.toString()}`).then(({ data }) => {
      setResults(data.items);
      setTotal(data.total);
    }).finally(() => setLoading(false));
  }, [filters, useNearMe, geo?.lat, geo?.lng]);

  const FilterPanel = (
    <div className="space-y-6">
      <div>
        <Label className="text-xs font-display font-semibold uppercase tracking-wider text-stone-700">Provider type</Label>
        <div className="mt-3 space-y-2">
          {TYPES.map((t) => (
            <button
              key={t.value}
              onClick={() => update({ type: t.value })}
              className={`w-full px-3 py-2 text-left text-sm rounded-lg transition ${filters.type === t.value ? "bg-[#064E3B] text-white" : "hover:bg-stone-100 text-stone-700"}`}
              data-testid={`filter-type-${t.value}`}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      <div>
        <Label className="text-xs font-display font-semibold uppercase tracking-wider text-stone-700">Service</Label>
        <Select value={filters.service || "any"} onValueChange={(v) => update({ service: v === "any" ? "" : v })}>
          <SelectTrigger className="mt-2" data-testid="filter-service-select"><SelectValue placeholder="Any service" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="any">Any service</SelectItem>
            {SERVICE_LIBRARY.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div>
        <Label className="text-xs font-display font-semibold uppercase tracking-wider text-stone-700">Minimum rating</Label>
        <div className="mt-3 grid grid-cols-3 gap-2">
          {[
            { v: "", l: "Any" }, { v: "4", l: "4★+" }, { v: "4.5", l: "4.5★+" },
          ].map((r) => (
            <button
              key={r.l}
              onClick={() => update({ min_rating: r.v })}
              className={`px-2 py-1.5 text-xs font-medium rounded-lg border transition ${filters.min_rating === r.v ? "bg-[#064E3B] text-white border-[#064E3B]" : "bg-white border-stone-200 hover:border-[#064E3B]/40 text-stone-700"}`}
            >{r.l}</button>
          ))}
        </div>
      </div>

      <div className="flex items-center gap-2 pt-2 border-t border-stone-200">
        <Checkbox
          id="verified"
          checked={filters.verified_only}
          onCheckedChange={(v) => update({ verified_only: !!v })}
          data-testid="filter-verified"
        />
        <Label htmlFor="verified" className="text-sm text-stone-700 cursor-pointer">Verified businesses only</Label>
      </div>
    </div>
  );

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 min-h-screen">
      {/* Search bar */}
      <div className="flex flex-col md:flex-row gap-3 mb-6">
        <div className="flex-1 flex items-center gap-3 bg-white border border-stone-200 rounded-xl px-4 py-2.5">
          <SearchIcon className="w-4 h-4 text-stone-400" />
          <Input
            value={filters.q}
            onChange={(e) => update({ q: e.target.value })}
            placeholder="Search providers, services, specialties..."
            className="border-0 px-0 focus-visible:ring-0 shadow-none"
            data-testid="search-input"
          />
        </div>
        <div className="flex-1 flex items-center gap-3 bg-white border border-stone-200 rounded-xl px-4 py-2.5">
          <span className="text-xs text-stone-400 uppercase font-semibold">City</span>
          <Input
            value={filters.city}
            onChange={(e) => update({ city: e.target.value })}
            placeholder="Any city"
            className="border-0 px-0 focus-visible:ring-0 shadow-none"
            data-testid="search-city-input"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-8">
        {/* Sidebar */}
        <aside className="hidden lg:block sticky top-20 self-start">
          <h2 className="font-display text-base font-semibold mb-4 flex items-center gap-2">
            <SlidersHorizontal className="w-4 h-4" /> Filters
          </h2>

          {/* Near-me banner */}
          {!useNearMe && (
            <div className="mb-5 p-3.5 rounded-xl bg-gradient-to-br from-[#064E3B] to-[#0a6b54] text-white" data-testid="nearme-banner">
              <div className="flex items-start gap-2 mb-2">
                <MapPinIcon className="w-4 h-4 mt-0.5 shrink-0" />
                <div>
                  <div className="text-sm font-semibold leading-tight">Show closest providers</div>
                  <div className="text-[11px] text-white/75 mt-0.5">
                    {geo?.city ? `Currently near ${geo.city}` : status === "locating" ? "Locating..." : "We'll calculate exact distance"}
                  </div>
                </div>
              </div>
              <button
                onClick={enableNearMe}
                disabled={status === "locating"}
                className="w-full bg-white text-[#064E3B] text-xs font-semibold py-1.5 rounded-full hover:bg-stone-100 transition inline-flex items-center justify-center gap-1.5"
                data-testid="use-my-location-btn"
              >
                {status === "locating" ? <Loader2 className="w-3 h-3 animate-spin" /> : <MapPinIcon className="w-3 h-3" />}
                Use my location
              </button>
            </div>
          )}
          {useNearMe && (
            <div className="mb-5 p-3 rounded-xl bg-[#10B981]/10 border border-[#10B981]/30 text-[#0a6b54] flex items-start gap-2" data-testid="nearme-active">
              <MapPinIcon className="w-4 h-4 mt-0.5 shrink-0" />
              <div className="flex-1 text-xs">
                <div className="font-semibold">Sorted by distance</div>
                <div className="text-stone-600 mt-0.5">Within 500 mi of you</div>
              </div>
              <button onClick={() => setUseNearMe(false)} className="text-stone-500 hover:text-stone-700" data-testid="nearme-clear">
                <XIcon className="w-3.5 h-3.5" />
              </button>
            </div>
          )}

          {FilterPanel}
        </aside>

        {/* Mobile filters */}
        <div className="lg:hidden">
          <Sheet>
            <SheetTrigger asChild>
              <Button variant="outline" className="w-full" data-testid="mobile-filters-trigger">
                <SlidersHorizontal className="w-4 h-4 mr-2" /> Filters
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="w-[300px] overflow-y-auto">
              <SheetHeader><SheetTitle className="font-display">Filters</SheetTitle></SheetHeader>
              <div className="mt-6">{FilterPanel}</div>
            </SheetContent>
          </Sheet>
        </div>

        {/* Results */}
        <div>
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-5">
            <div>
              <h1 className="font-display text-2xl font-semibold text-stone-900" data-testid="search-results-heading">
                {total} {total === 1 ? "provider" : "providers"} found
              </h1>
              {(filters.city || filters.service) && (
                <p className="text-sm text-stone-500 mt-1">
                  {filters.service && <>for <strong>{filters.service}</strong></>}{" "}
                  {filters.city && <>in <strong>{filters.city}</strong></>}
                </p>
              )}
            </div>
            <div className="flex items-center gap-2">
              <Select value={filters.sort} onValueChange={(v) => update({ sort: v })}>
                <SelectTrigger className="w-[160px]" data-testid="sort-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="featured">Featured</SelectItem>
                  <SelectItem value="rating">Highest Rated</SelectItem>
                  <SelectItem value="reviews">Most Reviewed</SelectItem>
                  <SelectItem value="newest">Newest</SelectItem>
                </SelectContent>
              </Select>
              <Tabs value={view} onValueChange={setView} className="hidden md:block">
                <TabsList>
                  <TabsTrigger value="list" data-testid="view-list"><List className="w-3.5 h-3.5" /></TabsTrigger>
                  <TabsTrigger value="split" data-testid="view-split">Split</TabsTrigger>
                  <TabsTrigger value="map" data-testid="view-map"><MapIcon className="w-3.5 h-3.5" /></TabsTrigger>
                </TabsList>
              </Tabs>
            </div>
          </div>

          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="bg-stone-100 rounded-2xl aspect-[4/3] animate-pulse" />
              ))}
            </div>
          ) : results.length === 0 ? (
            <div className="text-center py-16 bg-white rounded-2xl border border-stone-200" data-testid="no-results">
              <p className="font-display text-lg text-stone-900 mb-2">
                {useNearMe ? "No providers within 500 mi of you" : "No providers match your filters"}
              </p>
              <p className="text-sm text-stone-500 mb-5">
                {useNearMe
                  ? "Try turning off location filtering or searching a specific city."
                  : "Try clearing filters or searching a different city."}
              </p>
              <div className="flex flex-wrap gap-2 justify-center">
                {useNearMe && (
                  <Button variant="outline" onClick={() => setUseNearMe(false)} data-testid="clear-nearme-empty">
                    Turn off "Near me"
                  </Button>
                )}
                <Button variant="outline" onClick={() => { setUseNearMe(false); setParams({}); }} data-testid="clear-filters-empty">
                  Clear all filters
                </Button>
              </div>
            </div>
          ) : view === "map" ? (
            <ProvidersMap
              markers={results.map((p) => ({ id: p.id, lat: p.lat, lng: p.lng, name: p.name, city: p.city, state: p.state, slug: p.slug }))}
              height="700px"
              fitBounds
            />
          ) : view === "split" ? (
            <div className="grid grid-cols-1 xl:grid-cols-[1fr_400px] gap-5">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                {results.map((p) => (
                  <div key={p.id} className="relative">
                    <ProviderCard p={p} compact />
                    <button
                      onClick={(e) => { e.stopPropagation(); toggleCompare(p.slug); }}
                      className={`absolute bottom-3 right-3 z-10 inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-semibold uppercase tracking-wider transition ${compareSlugs.includes(p.slug) ? "bg-[#064E3B] text-white" : "bg-white/95 border border-stone-200 text-stone-700 hover:border-[#064E3B]/40"}`}
                      data-testid={`compare-toggle-${p.slug}`}
                    >
                      <GitCompare className="w-3 h-3" /> {compareSlugs.includes(p.slug) ? "Added" : "Compare"}
                    </button>
                  </div>
                ))}
              </div>
              <div className="hidden xl:block sticky top-20 self-start">
                <ProvidersMap
                  markers={results.map((p) => ({ id: p.id, lat: p.lat, lng: p.lng, name: p.name, city: p.city, state: p.state, slug: p.slug }))}
                  height="700px"
                  fitBounds
                />
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {results.map((p) => (
                <div key={p.id} className="relative">
                  <ProviderCard p={p} />
                  <button
                    onClick={(e) => { e.stopPropagation(); toggleCompare(p.slug); }}
                    className={`absolute bottom-3 right-3 z-10 inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-semibold uppercase tracking-wider transition ${compareSlugs.includes(p.slug) ? "bg-[#064E3B] text-white" : "bg-white/95 border border-stone-200 text-stone-700 hover:border-[#064E3B]/40"}`}
                  >
                    <GitCompare className="w-3 h-3" /> {compareSlugs.includes(p.slug) ? "Added" : "Compare"}
                  </button>
                </div>
              ))}
            </div>
          )}

          {compareSlugs.length >= 2 && (
            <div className="fixed bottom-5 left-1/2 -translate-x-1/2 z-40 bg-[#064E3B] text-white rounded-full shadow-2xl shadow-[#064E3B]/30 px-5 py-3 flex items-center gap-3" data-testid="compare-bar">
              <span className="text-sm font-medium">{compareSlugs.length} selected</span>
              <Link
                to={`/compare?${compareSlugs.map((s) => `p=${encodeURIComponent(s)}`).join("&")}`}
                className="px-4 py-1.5 rounded-full bg-white text-[#064E3B] text-sm font-semibold inline-flex items-center gap-1.5"
                data-testid="compare-go"
              >
                <GitCompare className="w-3.5 h-3.5" /> Compare now
              </Link>
              <button onClick={() => setCompareSlugs([])} className="text-white/70 hover:text-white text-sm">Clear</button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
